/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/useQaLevelActions.ts
 * FILE_ID: JOBHUB-QA-LEVEL-ACTIONS-001
 * PURPOSE: Level copy, paste, duplicate, and delete. Locations stay the area menu.
 */
import { useQueryClient } from '@tanstack/react-query'
import type { QaApi, QaFloor, QaPasteBody } from '../../qa/qaApi'
import { readQaClipboard, writeQaClipboard } from '../../qa/qaClipboard'
import { normalizeList } from '../../qa/qaApiHelpers'

export function useQaLevelActions({
  api,
  jobId,
  inspectionId,
  setError,
  onRename,
}: {
  api: QaApi
  jobId: number
  inspectionId: string | null
  setError: (error: string | null) => void
  onRename: (floorId: string) => void
}) {
  const queryClient = useQueryClient()

  function floors(): QaFloor[] {
    return normalizeList<QaFloor>(queryClient.getQueryData(['qa-floors', inspectionId]))
  }

  async function refresh() {
    await queryClient.invalidateQueries({ queryKey: ['qa-floors'] })
    await queryClient.invalidateQueries({ queryKey: ['qa-areas'] })
    await queryClient.invalidateQueries({ queryKey: ['qa-area-exec'] })
  }

  function copy(floorId: string, name: string) {
    writeQaClipboard({
      kind: 'level',
      ids: [floorId],
      label: name,
      jobId,
      inspectionId: inspectionId ?? '',
    })
  }

  async function placeCopy(createdId: string, targetId: string, position: 'above' | 'below') {
    const ids = floors().map((floor) => floor.id).filter((id) => id !== createdId)
    const index = ids.indexOf(targetId)
    const at = index < 0 ? ids.length : position === 'below' ? index + 1 : index
    ids.splice(Math.max(at, 1), 0, createdId)
    await api.reorderFloors(ids.map((id, sort_order) => ({ id, sort_order })))
  }

  return {
    copy,
    onRename,
    canMove(floorId: string, direction: -1 | 1) {
      const rows = floors()
      const index = rows.findIndex((floor) => floor.id === floorId)
      const next = index + direction
      return index > 0 && next > 0 && next < rows.length
    },
    move(floorId: string, direction: -1 | 1) {
      const rows = floors()
      const index = rows.findIndex((floor) => floor.id === floorId)
      const next = index + direction
      if (index <= 0 || next <= 0 || next >= rows.length) return
      const order = rows.map((floor, position) => ({ id: floor.id, sort_order: position }))
      const current = order[index]
      order[index] = order[next]
      order[next] = current
      order.forEach((row, position) => { row.sort_order = position })
      void api.reorderFloors(order).then(() => refresh())
    },
    async duplicate(floorId: string) {
      try {
        await api.duplicateFloor(floorId)
        await refresh()
      } catch (error) {
        setError(error instanceof Error ? error.message : 'Could not duplicate that level.')
      }
    },
    async remove(floorId: string, name: string) {
      if (!window.confirm(`Delete "${name}" and everything in it?`)) return
      try {
        await api.deleteFloor(floorId)
        await refresh()
      } catch (error) {
        setError(error instanceof Error ? error.message : 'Could not delete that level.')
      }
    },
    async insertBlank() {
      if (!inspectionId) return
      try {
        const created = await api.createFloor(inspectionId, 'New level', floors().length)
        await refresh()
        onRename(created.id)
      } catch (error) {
        setError(error instanceof Error ? error.message : 'Could not insert a level.')
      }
    },
    async pasteLevel(targetId: string, position: 'above' | 'below') {
      const clip = readQaClipboard()
      if (!clip || clip.kind !== 'level') return
      try {
        const created = await api.duplicateFloor(clip.ids[0])
        await placeCopy(created.id, targetId, position)
        await refresh()
      } catch (error) {
        setError(error instanceof Error ? error.message : 'Could not paste that level.')
      }
    },
    async pasteLocation(floorId: string, paste: (body: QaPasteBody) => Promise<{ created_ids: string[] }>) {
      const clip = readQaClipboard()
      if (!clip || clip.kind !== 'area' || !inspectionId) return
      try {
        // Reused from: packages/job-hub/src/jobs/tabs/job-qa/useQaPaste.ts (paste) and useQaTreeMoves.ts (move).
        const created = await paste({
          kind: 'area',
          source_ids: clip.ids,
          target_id: inspectionId,
          anchor_id: null,
          position: 'below',
        })
        if (created.created_ids.length) {
          await api.moveTreeNodes('area', created.created_ids, { parentId: floorId })
        }
        await refresh()
      } catch (error) {
        setError(error instanceof Error ? error.message : 'Could not paste that location.')
      }
    },
    copiedLevelLabel() {
      const clip = readQaClipboard()
      return clip?.kind === 'level' ? clip.label : null
    },
    copiedAreaLabel() {
      const clip = readQaClipboard()
      return clip?.kind === 'area' ? clip.label : null
    },
  }
}
